import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "reactstrap";
import { useSubscribeNewsletter } from "../../api/auth";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";
import { withTranslation } from "react-i18next";

// phone input
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import CustomHeader from "../../components/Auth/CustomHeader";
import { JOBARK_PRIVACY_URL } from "../../constants/workerOnboarding";
import { Link } from "react-router-dom";

const WorkerInterest = (props) => {
  const { t } = props;
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState("/worker-onboarding");

  document.title = t("workInterestTitle");

  const mutation = useSubscribeNewsletter();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      consent: false,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required(t("workInterestFullNameRequired")),
      email: Yup.string()
        .email(t("workInterestEmailInvalid"))
        .required(t("workInterestEmailRequired")),
      phoneNumber: Yup.string()
        .required(t("workInterestPhoneRequired"))
        .matches(/^[0-9]+$/, t("workInterestPhoneDigits"))
        .min(10, t("workInterestPhoneMin"))
        .max(15, t("workInterestPhoneMax")),
      consent: Yup.boolean().oneOf(
        [true],
        t("workInterestConsentRequired", "Please provide consent to continue")
      ),
    }),
    onSubmit: (values, { resetForm }) => {
      const payload = {
        ...values,
        role: "worker",
      };
      mutation.mutate(payload, {
        onSuccess: () => {
          toastr.success(t("workInterestToastSuccess"));
          const queryString = new URLSearchParams(values).toString();
          setOnboardingUrl(`/worker-onboarding?${queryString}`);
          setIsSuccessModalOpen(true);
          resetForm();
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
      });
    },
  });

  return (
    <React.Fragment>
      <CustomHeader />

      <Modal
        isOpen={isSuccessModalOpen}
        toggle={() => setIsSuccessModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setIsSuccessModalOpen(false)}>
          {t(
            "workInterestSuccessTitle",
            "Thank you for registering your interest!"
          )}
        </ModalHeader>
        <ModalBody>
          <p>
            {t(
              "workInterestSuccessBody1",
              "We've successfully received your details. We'll keep you updated with the latest JobArk news, platform updates and launch announcements until you're ready to get started."
            )}
          </p>
          <p className="mb-0">
            {t(
              "workInterestSuccessBody2",
              "In the meantime, if you have any questions, please don't hesitate to contact us."
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" tag="a" href="https://www.jobark.co.uk/">
            {t("workInterestReturnHome", "Return to Home")}
          </Button>
          <Button
            color="secondary"
            onClick={() => props.router.navigate(onboardingUrl)}
          >
            {t("workInterestGoOnboarding", "Go to Onboarding")}
          </Button>
        </ModalFooter>
      </Modal>

      <div className="auth-interest-page">
        <Container fluid className="py-5 px-9" style={{ maxWidth: 1600 }}>
          <Row className="justify-content-between align-items-start g-4">
            <Col lg={6} xl={7} className="px-4 px-lg-5">
              <p className="auth-interest-eyebrow mb-2">
                {t("workInterestEyebrow", "Welcome to JobArk")}
              </p>
              <h1
                className="display-5 fw-bold mb-4 text-dark"
                style={{ lineHeight: 1.1 }}
              >
                {t("workInterestHeroTitleTop")}{" "}
                <span className="text-secondary">
                  {t("workInterestHeroTitleBottom")}
                </span>
              </h1>

              <p className="mb-4 text-dark" style={{ maxWidth: 680 }}>
                {t("workInterestHeroDescription")}
              </p>

              <h2 className="h2 fw-bold mb-3 text-dark">
                {t("workInterestWhyTitle")}
              </h2>
              <ul className="list-unstyled mb-4" style={{ maxWidth: 740 }}>
                {[t("workInterestWhyPoint1"), t("workInterestWhyPoint2")].map(
                  (item, i) => (
                    <li key={i} className="d-flex align-items-start mb-2">
                      <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>

              <h2 className="h2 fw-bold mb-3 text-dark">
                {t("workInterestOnboardingNeedTitle")}
              </h2>
              <p className="mb-3 text-dark" style={{ maxWidth: 760 }}>
                {t("workInterestOnboardingNeedIntro")}
              </p>
              <ul className="list-unstyled mb-4" style={{ maxWidth: 760 }}>
                {[
                  t("workInterestOnboardingNeed1"),
                  t("workInterestOnboardingNeed2"),
                  t("workInterestOnboardingNeed3"),
                  t("workInterestOnboardingNeed4"),
                  t("workInterestOnboardingNeed5"),
                ].map((item, i) => (
                  <li
                    key={`onboarding-need-${i}`}
                    className="d-flex align-items-start mb-2"
                  >
                    <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mb-3 text-dark" style={{ maxWidth: 760 }}>
                {t("workInterestIdentityVerification")}
              </p>
              <p className="mb-4 text-dark" style={{ maxWidth: 760 }}>
                {t("workInterestEligibleAfterApproval")}
              </p>

              <h2 className="h2 fw-bold mb-3 text-dark">
                {t("workInterestAfterApprovalTitle")}
              </h2>
              <p className="mb-3 text-dark" style={{ maxWidth: 760 }}>
                {t("workInterestAfterApprovalIntro")}
              </p>
              <ul className="list-unstyled mb-4" style={{ maxWidth: 760 }}>
                {[
                  t("workInterestAfterApprovalPoint1"),
                  t("workInterestAfterApprovalPoint2"),
                  t("workInterestAfterApprovalPoint3"),
                  t("workInterestAfterApprovalPoint4"),
                ].map((item, i) => (
                  <li
                    key={`after-approval-${i}`}
                    className="d-flex align-items-start mb-2"
                  >
                    <span className="me-3 mt-1 rounded-circle d-inline-block auth-interest-bullet" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mb-0 text-dark" style={{ maxWidth: 760 }}>
                {t("workInterestDashboardSummary")}
              </p>
            </Col>

            <Col lg={6} xl={4} className="px-4">
              <Card className="auth-interest-form-card shadow-lg border border-gray-500">
                <div className="auth-interest-form-header">
                  <div className="text-white p-4 text-center">
                    <h5 className="text-white mb-1">
                      {t(
                        "workInterestRegisterHeadline",
                        "Register Your Interest (Optional)"
                      )}
                    </h5>
                    <p className="mb-0 text-white-50">
                      {t(
                        "workInterestRegisterSubtext",
                        "If you're not ready to complete full onboarding yet, register your interest and we'll guide you through the next steps."
                      )}
                    </p>
                  </div>
                </div>

                <CardBody>
                  <div>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {mutation.isSuccess && (
                        <Alert color="success">
                          {t(
                            "workInterestAlertSuccess",
                            "Successfully registered for the newsletter!"
                          )}
                        </Alert>
                      )}
                      {mutation.isError && (
                        <Alert color="danger">
                          {t("workInterestAlertError")}
                        </Alert>
                      )}

                      {/* Full Name */}
                      <div className="mb-3">
                        <Label className="form-label">
                          {t("workInterestFullNameLabel")}
                        </Label>
                        <Input
                          name="fullName"
                          type="text"
                          placeholder={t("workInterestFullNamePlaceholder")}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.fullName || ""}
                          invalid={
                            validation.touched.fullName &&
                            !!validation.errors.fullName
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.fullName}
                        </FormFeedback>
                      </div>

                      {/* Email */}
                      <div className="mb-3">
                        <Label className="form-label">
                          {t("workInterestEmailLabel")}
                        </Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder={t("workInterestEmailPlaceholder")}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email &&
                            !!validation.errors.email
                          }
                        />
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      </div>

                      {/* Phone Number */}
                      <div className="mb-3">
                        <Label className="form-label">
                          {t("workInterestPhoneLabel")}
                        </Label>
                        <PhoneInput
                          country="gb"
                          enableSearch
                          containerClass="w-100"
                          inputProps={{
                            name: "phoneNumber",
                            required: true,
                            "aria-label": t("workInterestPhoneAria"),
                          }}
                          value={validation.values.phoneNumber}
                          onBlur={() =>
                            validation.setFieldTouched("phoneNumber", true)
                          }
                          onChange={(value) =>
                            validation.setFieldValue("phoneNumber", value)
                          }
                        />
                        {validation.touched.phoneNumber &&
                          validation.errors.phoneNumber && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.phoneNumber}
                            </div>
                          )}
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            id="consent"
                            name="consent"
                            type="checkbox"
                            checked={validation.values.consent}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.consent &&
                              !!validation.errors.consent
                            }
                          />
                          <Label className="form-check-label" htmlFor="consent">
                            {t(
                              "workInterestConsentLabelPrefix",
                              "I consent to JobArk processing my data in line with the"
                            )}{" "}
                            <a
                              href={JOBARK_PRIVACY_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t(
                                "workInterestConsentLabelLink",
                                "Privacy Policy"
                              )}
                            </a>
                            .
                          </Label>
                        </div>
                        {validation.touched.consent &&
                          validation.errors.consent && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.consent}
                            </div>
                          )}
                      </div>

                      <div className="d-grid">
                        <button
                          className="btn btn-secondary btn-lg"
                          type="submit"
                          disabled={mutation.isPending}
                        >
                          {mutation.isPending
                            ? t("workInterestSubmitting")
                            : t(
                                "workInterestRegisterButton",
                                "Register Interest"
                              )}
                        </button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
                <div className="auth-interest-signup-footer">
                  <Link
                    to="/worker-onboarding"
                    className="auth-interest-signup-link"
                  >
                    {t("workInterestStartOnboardingButton", "Sign up now")}
                  </Link>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

WorkerInterest.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.shape({
    navigate: PropTypes.func.isRequired
  }).isRequired
};

const WorkerInterestWithProviders = withRouter(withTranslation()(WorkerInterest));

export default WorkerInterestWithProviders;
